import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { lookup } from '../helpers/array.js';

function getActiveIndex(items) {
  return function (active) {
    return items.findIndex(function (item) {
      return item.value === active;
    });
  };
}
function useHighlightActive(enabled, items, selected, onSelect, onClose, options) {
  var disableFocusTransfer = options.disableFocusTransfer,
    onKeyboardNavigateTopOut = options.onKeyboardNavigateTopOut,
    onActiveChange = options.onActiveChange,
    activateFirstItem = options.activateFirstItem,
    selectWithSpacebar = options.selectWithSpacebar,
    disableActive = options.disableActive,
    _options$disableMouse = options.disableMouseMoveHandling,
    disableMouseMoveHandling = _options$disableMouse === undefined ? false : _options$disableMouse;
  // for multiselect do not set active item by default
  var activeValue = Array.isArray(selected) ? undefined : selected;
  var activeRef = React.useRef(null);
  var _React$useState = React.useState(activeValue),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    active = _React$useState2[0],
    setActive = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    keyboardNavigation = _React$useState4[0],
    setKeyboardNavigation = _React$useState4[1];
  var lastKeyboardNavigation = React.useRef(0);
  React.useEffect(function () {
    if (enabled) {
      if (activeValue) {
        setActive(activeValue);
      } else {
        // istanbul ignore next
        items[0] && activateFirstItem && setActive(items[0].value);
      }
    } else {
      setActive(undefined);
    }
  }, [enabled, setActive]);
  React.useEffect(function () {
    if (disableActive) {
      // istanbul ignore next
      setActive(undefined);
    }
  }, [disableActive, setActive]);

  // focus active item
  React.useEffect(function () {
    if (!disableFocusTransfer && enabled) {
      var _activeRef$current;
      activeRef === null || activeRef === undefined || (_activeRef$current = activeRef.current) === null || _activeRef$current === undefined || _activeRef$current.focus();
    }
  }, [active, activeRef.current, disableFocusTransfer, enabled]);
  React.useEffect(function () {
    if (active)
      // istanbul ignore next
      onActiveChange && (activeRef === null || activeRef === undefined ? undefined : activeRef.current) && onActiveChange(activeRef.current);else onActiveChange && onActiveChange(undefined);
  }, [active, activeRef.current, onActiveChange]);

  // Set active item on mouse hover
  var handleMouseMove = React.useCallback(function (value) {
    // If there's no change, skip expensive state update (this callback might
    // run many tens of times per second)
    if (value === active && keyboardNavigation === false || disableMouseMoveHandling) return;

    // Ignore event if there has been a keyboard navigation event in the last 500ms
    if (new Date().getTime() - lastKeyboardNavigation.current < 500) return;
    setKeyboardNavigation(false);
    setActive(value);
  }, [setActive, active, keyboardNavigation, setKeyboardNavigation, disableMouseMoveHandling]);
  var handleKeyDown = React.useCallback(function (e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      var index = getActiveIndex(items)(active);
      var newIndex = Math.min(index + 1, items.length - 1);
      var item = lookup(items, newIndex);
      setKeyboardNavigation(true);
      lastKeyboardNavigation.current = new Date().getTime();
      if (item) setActive(item.value);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var _index = getActiveIndex(items)(active);
      if (_index - 1 < 0) {
        if (!onKeyboardNavigateTopOut) {
          return;
        }
        onKeyboardNavigateTopOut();
        setActive(undefined);
      } else {
        var _item = lookup(items, _index - 1);
        // istanbul ignore else
        if (_item) setActive(_item.value);
        setKeyboardNavigation(true);
        lastKeyboardNavigation.current = new Date().getTime();
      }
    } else if (e.key === 'Enter' || selectWithSpacebar && e.key === ' ' || selectWithSpacebar && e.key === 'Spacebar') {
      if (!active) return;
      e.preventDefault();
      onSelect(active);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [active, onClose, setActive, setKeyboardNavigation, items, onSelect, onKeyboardNavigateTopOut, lastKeyboardNavigation, selectWithSpacebar]);

  // Set active item on keyboard list navigation
  React.useEffect(function () {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }
    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
  return {
    active: active,
    setActive: setActive,
    handleMouseMove: handleMouseMove,
    keyboardNavigation: keyboardNavigation,
    setKeyboardNavigation: setKeyboardNavigation,
    activeRef: activeRef
  };
}

export { useHighlightActive };
